import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { StarRating } from "@/components/StarRating"
import { useToast } from "@/hooks/use-toast"
import { supabase } from "@/integrations/supabase/client"
import { detectDeviceInfo } from "@/utils/deviceDetection"
import SEOHead from "@/components/SEOHead"
import { Heart, CheckCircle, MessageSquare, Home } from "lucide-react"
import { trackEvent, trackConversion } from '@/components/GoogleAnalytics';
import { trackEventMp, trackConversionMp } from '@/components/Mixpanel';
import { trackEvent as trackEventHj } from '@/components/Hotjar';

export default function Feedback() {
  const { subscriptionId } = useParams<{ subscriptionId: string }>()
  const { toast } = useToast()
  const { t } = useTranslation()
  const navigate = useNavigate()
  
  const [rating, setRating] = useState(0)
  const [message, setMessage] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [userEmail, setUserEmail] = useState("")

  useEffect(() => {
    if (subscriptionId) {
      // Try to get user email for personalization (optional)
      const fetchUserEmail = async () => {
        try {
          const { data } = await supabase
            .from('newsletter_subscriptions')
            .select('email')
            .eq('id', subscriptionId)
            .single()
          
          if (data?.email) {
            setUserEmail(data.email)
          }
        } catch (error) {
          // Silently fail - not critical
        }
      }
      
      fetchUserEmail()
    }
  }, [subscriptionId])

  const getRatingMessage = (rating: number) => {
    switch (rating) {
      case 1: return t('feedback.ratingMessages.rating1')
      case 2: return t('feedback.ratingMessages.rating2')
      case 3: return t('feedback.ratingMessages.rating3')
      case 4: return t('feedback.ratingMessages.rating4')
      case 5: return t('feedback.ratingMessages.rating5')
      default: return t('feedback.ratingMessages.default')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (rating === 0) {
      toast({
        title: t('feedback.toasts.ratingRequired.title'),
        description: t('feedback.toasts.ratingRequired.description'),
        variant: "destructive",
      })
      return
    }

    setIsLoading(true)

    try {
      // Track analytics event
      const eventParams = {
        app_version: '3.0',
        layout_version: 'interactive_preview',
        event_category: 'user_action',
        rating: rating,
        has_comment: message.trim().length > 0,
        comment_length: message.trim().length
      };
      
      trackEvent('feedback_submitted', eventParams);
      trackEventMp('feedback_submitted', eventParams);
      trackEventHj('feedback_submitted');

      const deviceInfo = detectDeviceInfo()
      
      // Get location info (optional)
      let locationInfo = null
      try {
        const geoResponse = await fetch('https://ipapi.co/json/')
        if (geoResponse.ok) {
          const geoData = await geoResponse.json()
          locationInfo = {
            ip_address: geoData.ip || 'unknown',
            country: geoData.country_name || null,
            region: geoData.region || null,
            city: geoData.city || null,
            timezone: geoData.timezone || null,
            latitude: geoData.latitude || null,
            longitude: geoData.longitude || null
          }
        }
      } catch (error) {
        console.log('Could not get location info:', error)
      }

      const { data, error } = await supabase.functions.invoke('submit-feedback', {
        body: {
          subscription_id: subscriptionId,
          rating,
          message: message.trim() || null,
          device_info: {
            user_agent: deviceInfo.userAgent,
            browser_name: deviceInfo.browserName,
            browser_version: deviceInfo.browserVersion,
            device_type: deviceInfo.deviceType,
            operating_system: deviceInfo.operatingSystem
          },
          location_info: locationInfo
        }
      })

      if (error) {
        throw error
      }

      if (data?.error) {
        throw new Error(data.error)
      }

      setIsSubmitted(true)
      toast({
        title: t('feedback.toasts.success.title'),
        description: t('feedback.toasts.success.description'),
      })

    } catch (error: any) {
      console.error('Error submitting feedback:', error)
      toast({
        title: t('feedback.toasts.error.title'),
        description: error.message || t('feedback.toasts.error.description'),
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted/50 flex items-center justify-center p-4">
        <SEOHead />
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            <div className="mx-auto mb-4 w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl">{t('feedback.success.title')}</CardTitle>
            <CardDescription>
              {t('feedback.success.description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-center">
                <StarRating rating={rating} onRatingChange={() => {}} disabled />
              </div>
              {rating >= 4 && (
                <p className="text-sm text-muted-foreground">
                  {t('feedback.success.highRatingMessage')}
                </p>
              )}
              {rating <= 3 && (
                <p className="text-sm text-muted-foreground">
                  {t('feedback.success.lowRatingMessage')}
                </p>
              )}
            </div>
            <Button 
              variant="outline" 
              onClick={() => {
                // Track analytics event
                const eventParams = {
                  app_version: '3.0',
                  layout_version: 'interactive_preview',
                  event_category: 'user_action',
                  rating: rating
                };
                
                trackEvent('feedback_return_home', eventParams);
                trackEventMp('feedback_return_home', eventParams);
                trackEventHj('feedback_return_home');
                
                navigate('/');
              }}
              className="w-full mt-6"
            >
              <Home className="w-4 h-4 mr-2" />
              {t('feedback.success.backButton')}
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/50 flex items-center justify-center p-4">
      <SEOHead />
      
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
            <Heart className="w-8 h-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">{t('feedback.form.title')}</CardTitle>
          <CardDescription>
            {userEmail ? t('feedback.form.descriptionWithEmail') : t('feedback.form.descriptionGeneral')}
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Rating Section */}
            <div className="text-center space-y-3">
              <label className="block text-sm font-medium">
                {t('feedback.form.ratingLabel')}
              </label>
              <StarRating 
                rating={rating} 
                onRatingChange={(newRating) => {
                  // Track analytics event
                  const eventParams = {
                    app_version: '3.0',
                    layout_version: 'interactive_preview',
                    event_category: 'engagement',
                    rating: newRating,
                    previous_rating: rating
                  };
                  
                  trackEvent('feedback_rating_selected', eventParams);
                  trackEventMp('feedback_rating_selected', eventParams);
                  trackEventHj('feedback_rating_selected');
                  
                  setRating(newRating);
                }} 
                size="lg"
                disabled={isLoading}
              />
              {rating > 0 && (
                <p className="text-sm text-muted-foreground animate-in slide-in-from-top-2 duration-300">
                  {getRatingMessage(rating)}
                </p>
              )}
            </div>

            {/* Message Section */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-muted-foreground" />
                <label className="text-sm font-medium">
                  {t('feedback.form.commentsLabel')}
                </label>
              </div>
              <Textarea
                value={message}
                onChange={(e) => {
                  const newMessage = e.target.value;
                  
                  // Track analytics event when user starts typing
                  if (message.length === 0 && newMessage.length > 0) {
                    const eventParams = {
                      app_version: '3.0',
                      layout_version: 'interactive_preview',
                      event_category: 'engagement',
                      rating: rating
                    };
                    
                    trackEvent('feedback_comment_started', eventParams);
                    trackEventMp('feedback_comment_started', eventParams);
                    trackEventHj('feedback_comment_started');
                  }
                  
                  setMessage(newMessage);
                }}
                placeholder={t('feedback.form.placeholder')}
                className="min-h-[100px] resize-none"
                maxLength={500}
                disabled={isLoading}
              />
              <div className="text-xs text-muted-foreground text-right">
                {message.length}/500
              </div>
            </div>

            {/* Submit Button */}
            <Button 
              type="submit" 
              className="w-full" 
              disabled={rating === 0 || isLoading}
            >
              {isLoading ? t('feedback.form.submittingButton') : t('feedback.form.submitButton')}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              {t('feedback.form.footerText')}
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}